import React, { useState } from 'react';
import { Popover, Tooltip } from 'antd';
import { InfoCircleOutlined } from '@ant-design/icons';
import { ICONS } from 'assets/icons';

/**
 * RecentBroadcastsCard Component
 *
 * Displays recent broadcasts with delivery and open rates
 * Features:
 * - Filter dropdown (Last 3, 5, 7, 9 broadcasts) using FilterHome design
 * - Capitalized broadcast names
 * - Delivery rate: delivered / sent
 * - Open rate: opened / delivered
 * - Info tooltips explaining metrics
 */
const RecentBroadcastsCard = ({
  broadcasts,
  loading,
  rangeCount = 3,
  onRangeChange,
  formatRate,
}) => {
  const [isPopoverVisible, setPopoverVisible] = useState(false);

  // Filter options matching user's requirement
  const filterOptions = [
    { id: 1, label: 'Last 3 broadcasts', value: 3 },
    { id: 2, label: 'Last 5 broadcasts', value: 5 },
    { id: 3, label: 'Last 7 broadcasts', value: 7 },
    { id: 4, label: 'Last 9 broadcasts', value: 9 },
  ];

  const selectedFilter =
    filterOptions.find((opt) => opt.value === rangeCount)?.label ||
    'Last 5 broadcasts';

  const handleFilterChange = (value) => {
    setPopoverVisible(false);
    onRangeChange(value);
  };

  // Filter dropdown content component
  const FilterDropdownContent = () => (
    <div className="p-2">
      {filterOptions.map((option) => (
        <div
          key={option.id}
          className="px-3 py-2 hover:bg-gray-50 cursor-pointer rounded transition-colors border-b last:border-b-0"
          onClick={() => handleFilterChange(option.value)}
        >
          <span className="text-sm text-[var(--textBlack)]">
            {option.label}
          </span>
        </div>
      ))}
    </div>
  );

  // Info tooltip content
  const deliveryRateTooltip = (
    <div className="max-w-xs">
      <p className="font-semibold mb-1">Delivery Rate</p>
      <p className="text-xs">
        The percentage of messages that were successfully delivered to
        recipients out of total messages sent.
      </p>
      <p className="text-xs mt-1">Formula: (Delivered ÷ Sent) × 100</p>
    </div>
  );

  const openRateTooltip = (
    <div className="max-w-xs">
      <p className="font-semibold mb-1">Open Rate</p>
      <p className="text-xs">
        The percentage of delivered messages that were opened/read by
        recipients.
      </p>
      <p className="text-xs mt-1">Formula: (Opened ÷ Delivered) × 100</p>
    </div>
  );

  const visibleBroadcasts = broadcasts.slice(0, rangeCount || broadcasts.length);

  // Aggregate for quick insight bar
  const totals = visibleBroadcasts.reduce(
    (acc, item) => {
      const sentCount = item?.total_count || 0;
      const deliveredCount =
        item?.delivered_count || item?.delivered || item?.deliveredCount || 0;
      const openedCount =
        item?.opened_count || item?.opened || item?.openedCount || 0;
      acc.sent += sentCount;
      acc.delivered += deliveredCount;
      acc.opened += openedCount;
      return acc;
    },
    { sent: 0, delivered: 0, opened: 0 },
  );

  const funnelPercent = {
    delivered: formatRate(totals.delivered, totals.sent),
    opened: formatRate(totals.opened, totals.delivered),
  };

  return (
    <div className="col-span-12 lg:col-span-7 bg-[var(--surface)] rounded-2xl p-6 shadow-[0px_20px_50px_-32px_rgba(15,23,42,0.35)] border border-[var(--surface-border)]">
      {/* Header */}
      <div className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between mb-6">
        <div>
          <p className="text-[18px] font-semibold text-[var(--textBlack)]">
            Recent broadcasts
          </p>
          <p className="text-sm text-[var(--font-600)]">
            Delivery and open rates for your latest sends.
          </p>
        </div>

        {/* Filter Dropdown */}
        <Popover
          content={<FilterDropdownContent />}
          trigger="click"
          open={isPopoverVisible}
          onOpenChange={setPopoverVisible}
          placement="bottomRight"
        >
          <div className="flex items-center border border-[#E5E7EB] cursor-pointer px-3.5 py-2 rounded-md transition-colors bg-[var(--surface-muted)] hover:border-[var(--accent-2)]">
            <p className="text-sm text-[var(--textBlack)]">{selectedFilter}</p>
            <img
              src={ICONS?.chevronDownCarousel}
              className="ml-2 w-4 h-4"
              alt="dropdown-icon"
            />
          </div>
        </Popover>
      </div>

      {/* Summary funnel */}
      <div className="grid grid-cols-2 gap-3 mb-5">
        <div className="rounded-xl bg-[var(--surface-muted)] p-4 border border-[var(--surface-border)]">
          <div className="flex items-center justify-between text-xs text-[var(--font-600)]">
            <span>Delivered</span>
            <Tooltip title="Percentage of messages successfully delivered out of total sent" placement="top">
              <InfoCircleOutlined className="text-[var(--font-600)] text-xs" />
            </Tooltip>
          </div>
          <p className="text-[20px] font-semibold text-[var(--textBlack)] leading-7">
            {funnelPercent.delivered}
          </p>
          <div className="mt-2 h-2 rounded-full bg-[#e5e7eb] overflow-hidden">
            <div
              className="h-full rounded-full bg-[var(--accent-2)]"
              style={{
                width: totals.sent
                  ? `${Math.min(
                      100,
                      (Number(totals.delivered) / Number(totals.sent || 1)) *
                        100,
                    ).toFixed(0)}%`
                  : '0%',
                transition: 'width 250ms ease-out',
              }}
            />
          </div>
        </div>
        <div className="rounded-xl bg-[var(--surface-muted)] p-4 border border-[var(--surface-border)]">
          <div className="flex items-center justify-between text-xs text-[var(--font-600)]">
            <span>Opened</span>
            <Tooltip title="Percentage of delivered messages that were read" placement="top">
              <InfoCircleOutlined className="text-[var(--font-600)] text-xs" />
            </Tooltip>
          </div>
          <p className="text-[20px] font-semibold text-[var(--textBlack)] leading-7">
            {funnelPercent.opened}
          </p>
          <div className="mt-2 h-2 rounded-full bg-[#e5e7eb] overflow-hidden">
            <div
              className="h-full rounded-full bg-[var(--accent)]"
              style={{
                width: totals.delivered
                  ? `${Math.min(
                      100,
                      (Number(totals.opened) / Number(totals.delivered || 1)) *
                        100,
                    ).toFixed(0)}%`
                  : '0%',
                transition: 'width 250ms ease-out',
              }}
            />
          </div>
        </div>
      </div>

      {/* List */}
      <div>
        {loading ? (
          <div
            className="flex flex-col gap-3 overflow-y-auto pr-1"
            style={{ maxHeight: '240px' }} // roughly 3 rows visible, then scroll
          >
            {[...Array(3)].map((_, index) => (
              <div
                key={`broadcast-skeleton-${index}`}
                className="h-14 rounded-lg bg-[#F2F4F7] animate-pulse"
              />
            ))}
          </div>
        ) : visibleBroadcasts.length > 0 ? (
          <div className="flex flex-col gap-3 min-h-[260px] max-h-[260px] overflow-y-auto">
            {/* Table Header */}
            <div className="flex items-center gap-4 px-3 mb-1 mt-[5px]">
              <div className="w-10 flex-shrink-0" /> {/* Spacer for icon */}
              <div className="flex-1 min-w-0">
                <span className="text-[11px] font-semibold text-[var(--font-600)] uppercase tracking-wider">
                  Broadcast details
                </span>
              </div>
              <div className="flex items-center gap-4 text-right">
                <span className="w-[6rem] text-[11px] font-semibold text-[var(--font-600)] uppercase tracking-wider text-center">
                  Delivery rate
                </span>
                <span className="w-[6rem] text-[11px] font-semibold text-[var(--font-600)] uppercase tracking-wider text-center">
                  Open rate
                </span>
              </div>
            </div>

            {visibleBroadcasts.map((item) => {
              const sentCount = item?.total_count || 0;
              const deliveredCount =
                item?.delivered_count ||
                item?.delivered ||
                item?.deliveredCount ||
                0;
              const openedCount =
                item?.opened_count || item?.opened || item?.openedCount || 0;

              const broadcastName =
                item?.name || item?.campaign_name || 'Unnamed Broadcast';
              const capitalizedName =
                broadcastName.charAt(0).toUpperCase() + broadcastName.slice(1);

              return (
                <div
                  key={item?.id}
                  className="flex items-center gap-4 bg-[var(--surface-muted)] rounded-xl px-3 py-3 border border-[var(--surface-border)] hover:border-[var(--accent-2)] transition-colors"
                >
                  <div className="w-[36px] h-[36px] rounded-xl bg-white grid place-items-center text-sm font-semibold text-[var(--accent-2)] shadow-sm">
                    {item?.display_emoji || '📣'}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p
                      className="font14px-600 text-[var(--textBlack)] truncate"
                      title={capitalizedName}
                    >
                      {capitalizedName}
                    </p>
                    <p className="text-xs text-[var(--font-600)]">
                      Sent {sentCount.toLocaleString()} · Delivered{' '}
                      {deliveredCount.toLocaleString()}
                    </p>
                  </div>
                  <div className="flex items-center gap-4 text-sm font-semibold text-[var(--textBlack)]">
                    <span className="w-[6rem] text-center">
                      {formatRate(deliveredCount, sentCount)}
                    </span>
                    <span className="w-[6rem] text-center text-[var(--accent)]">
                      {formatRate(openedCount, deliveredCount)}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="border border-dashed border-[#E8E8EA] rounded-xl p-8 text-center bg-[var(--surface-muted)]">
            <p className="text-[var(--font-600)] text-sm">
              No broadcasts found. Create one to start engaging customers.
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default RecentBroadcastsCard;
